import { inject, injectable } from "inversify";
import * as THREE from "three";
import { DomElementsService, type IService, Logger, type PointerPosition } from "@designboticteam/core";
import { RAYCASTABLE_LAYER } from "../../layers";
import { CameraService } from "../../stage/CameraService";
import { SceneService } from "../../stage/SceneService";

type RaycastHit = {
	object: THREE.Object3D;
	worldPosition: THREE.Vector3;
	screenPosition: { x: number; y: number };
};

const GROUND_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
// World metres, and only honoured by THREE.Line.
const LINE_RAYCAST_THRESHOLD = 2;
// CSS pixels, added to the material's linewidth. Line2 hit-tests in screen space and three does not
// create `params.Line2` at all, so without this a 2px outline is only grabbable within its own 2px.
const LINE2_RAYCAST_THRESHOLD_PX = 6;

function getPolygonOffsetUnits(object: THREE.Object3D): number {
	const material = (object as THREE.Mesh).material;

	if (material instanceof THREE.Material && material.polygonOffset === true) {
		return material.polygonOffsetUnits ?? 0;
	}

	return 0;
}

@injectable()
class RaycastService implements IService {
	readonly serviceName = "🎯 RaycastService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});

	#raycaster = new THREE.Raycaster();

	constructor(
		@inject(DomElementsService) private domElements: DomElementsService,
		@inject(CameraService) private cameraService: CameraService,
		@inject(SceneService) private sceneService: SceneService,
	) {}

	public initialize(): void {
		this.#raycaster.layers.set(RAYCASTABLE_LAYER);
		this.#raycaster.params.Line.threshold = LINE_RAYCAST_THRESHOLD;
		this.#raycaster.params.Line2 = { threshold: LINE2_RAYCAST_THRESHOLD_PX };

		this.logger.log("initialized");
	}

	public dispose(): void {
		this.logger.log("disposed");
	}

	/** Cast a ray from screen position and return the first selectable object hit. */
	public pickObjectAt(position: PointerPosition): RaycastHit | null {
		const normalizedViewport = this.toNormalizedViewport(position);
		this.#raycaster.setFromCamera(normalizedViewport, this.cameraService.camera);

		// Raycaster automatically filters by SELECTABLE_LAYER
		const intersects = this.#raycaster.intersectObjects(this.sceneService.scene.children, true);

		if (intersects.length === 0) {
			return null;
		}

		// For hits at the same depth (overlapping zones), prefer the one drawn on top.
		let picked = intersects[0];

		for (const hit of intersects) {
			if (hit.distance - intersects[0].distance > 0.000_001) {
				break;
			}

			if (getPolygonOffsetUnits(hit.object) > getPolygonOffsetUnits(picked.object)) {
				picked = hit;
			}
		}

		return {
			object: picked.object,
			worldPosition: picked.point,
			screenPosition: this.toLocalScreen(position),
		};
	}

	/** Cast a ray from screen position and return the intersection point with the ground plane. */
	public pickGroundPointAt(position: PointerPosition): THREE.Vector3 | null {
		const normalizedViewport = this.toNormalizedViewport(position);
		this.#raycaster.setFromCamera(normalizedViewport, this.cameraService.camera);

		const intersection = new THREE.Vector3();
		const hit = this.#raycaster.ray.intersectPlane(GROUND_PLANE, intersection);

		return hit ? intersection : null;
	}

	/** Convert screen pixel coordinates to normalized device coordinates [-1, 1]. */
	private toNormalizedViewport(position: PointerPosition): THREE.Vector2 {
		const rect = this.domElements.containerElement.getBoundingClientRect();
		const x = ((position.x - rect.left) / rect.width) * 2 - 1;
		const y = -((position.y - rect.top) / rect.height) * 2 + 1;

		return new THREE.Vector2(x, y);
	}

	/** Convert page coordinates to container-relative pixel position. */
	private toLocalScreen(position: PointerPosition): { x: number; y: number } {
		const rect = this.domElements.containerElement.getBoundingClientRect();

		return {
			x: position.x - rect.left,
			y: position.y - rect.top,
		};
	}
}

export type { RaycastHit };
export { RaycastService };
